import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { Search, MapPin, Filter, Sparkles } from 'lucide-react';

const SPORT_ICONS = {
  Football: '⚽', Cricket: '🏏', Badminton: '🏸', Tennis: '🎾', Basketball: '🏀',
  Hockey: '🏑', Volleyball: '🏐', Pickleball: '🏓', 'Table Tennis': '🏓',
  Squash: '🎾', Athletics: '🏃', Other: '🏟️',
};

export default function ArenaxVenues() {
  const [params, setParams] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get('q') || '');
  const sport = params.get('sport') || '';
  const city = params.get('city') || '';

  useEffect(() => {
    setLoading(true);
    const qry = new URLSearchParams();
    if (q) qry.set('q', q);
    if (sport) qry.set('sport', sport);
    if (city) qry.set('city', city);
    api.get(`/arenax/venues?${qry.toString()}`).then(r => setVenues(r.data.venues || [])).finally(() => setLoading(false));
  }, [q, sport, city]);

  const onSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (q) p.set('q', q); else p.delete('q');
    setParams(p);
  };

  return (
    <ArenaxLayout>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
          <div>
            <div className="text-[11px] tracking-[0.4em] text-cyan-300 font-bold mb-2">DISCOVERY</div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-100">Find your arena</h1>
            <p className="text-sm text-slate-400 mt-1">Premium turfs, courts, and grounds across India.</p>
          </div>
          <form onSubmit={onSearch} className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={q} onChange={e => setQ(e.target.value)}
                data-testid="arenax-venues-search"
                placeholder="Search by name or city..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-700 focus:border-cyan-500 focus:outline-none text-sm text-slate-100 placeholder:text-slate-600" />
            </div>
          </form>
        </div>

        {(sport || city) && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-[11px] text-slate-500 uppercase tracking-wider">Filters:</span>
            {sport && <FilterChip label={`Sport: ${sport}`} onClear={() => { const p = new URLSearchParams(params); p.delete('sport'); setParams(p); }} />}
            {city && <FilterChip label={`City: ${city}`} onClear={() => { const p = new URLSearchParams(params); p.delete('city'); setParams(p); }} />}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-slate-500">Loading...</div>
        ) : venues.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-cyan-500/20 p-14 text-center">
            <Sparkles className="h-10 w-10 text-cyan-400/50 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-200 mb-2">No venues match your filter yet</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Venues come online weekly as Out-Play academies enable ArenaX. Check back soon.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="arenax-venues-grid">
            {venues.map(v => (
              <Link key={v.id} to={`/arenax/venues/${v.slug || v.id}`}
                className="block rounded-2xl border border-slate-800 hover:border-cyan-500/40 overflow-hidden transition-all hover:-translate-y-1 group"
                style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }}
                data-testid={`arenax-venue-${v.id}`}>
                <div className="aspect-[16/9] relative overflow-hidden" style={{
                  background: v.cover_image_url ? `url(${v.cover_image_url}) center/cover` : 'linear-gradient(135deg, #0e7490, #f59e0b)',
                }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent" />
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1">
                    {(v.sports_supported || []).slice(0, 3).map(s => (
                      <span key={s} className="text-[10px] font-bold px-2 py-1 rounded-md bg-cyan-500/20 backdrop-blur-md border border-cyan-500/30 text-cyan-200">
                        {SPORT_ICONS[s] || '🏟️'} {s}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-base font-bold text-slate-100 mb-1 group-hover:text-cyan-300 transition-colors">{v.name}</div>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
                    <MapPin className="h-3 w-3" />{v.city || 'India'}
                  </div>
                  <div className="text-xs text-slate-400 line-clamp-2">{v.tagline || v.description || ''}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </ArenaxLayout>
  );
}

function FilterChip({ label, onClear }) {
  return (
    <button onClick={onClear} className="text-[11px] px-2.5 py-1 rounded-md border border-cyan-500/30 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20">
      {label} ×
    </button>
  );
}
